import { useLoadingOverlay } from "@/Shared/Hooks/useLoadingOverlay";
import { useLocationsSaved } from "@/Shared/Locations/Hooks/useLocationsSaved";
import { useSavedLocationsService } from "@/Shared/Locations/Services/LocationsSavedService";
import { useShipmentService } from "@/Services/ShipmentService";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { ReactNode } from "react";
import { useUpdatedBOLDialog } from "../Dialogs/ViewShipmentsUpdatedBOLDialog";
import { useViewShipmentsModifyReferenceAndNotesAfterBookingDialogImplementation } from "./useViewShipmentsModifyReferenceAndNotesAfterBookingDialogImplementation";
import { locationsFindSavedLocation } from "@/Shared/Locations/Helpers/locationsFindSavedLocation";
import { useLocationsCreateSavedLocationFromShipment } from "@/Shared/Locations/Hooks/useLocationsCreateSavedLocationFromShipment";

function findSavedLocationId(
  locationContext: LocationContext,
  shipment: Shipment,
  savedLocations: Array<SavedLocation>,
) {
  const location =
    locationContext === LocationContext.Pickup
      ? shipment.pickupLocation
      : shipment.deliveryLocation;

  return locationsFindSavedLocation(savedLocations, location)?.savedLocationId;
}

export function useViewShipmentsModifyReferenceAndNotesAfterBookingDialog(
  shipment: Shipment,
  locationContext: LocationContext,
): { show: () => Promise<boolean>; dialogs: ReactNode } {
  const location =
    locationContext === LocationContext.Pickup
      ? shipment.pickupLocation
      : shipment.deliveryLocation;

  const locationType = location.locationType;
  const distributionWarehouseBrand = location.distributionWarehouseBrand;

  const referenceAndNotesDialog =
    useViewShipmentsModifyReferenceAndNotesAfterBookingDialogImplementation(
      locationContext,
      locationType,
    );
  const updatedBOLDialog = useUpdatedBOLDialog();
  const shipmentService = useShipmentService();
  const savedLocationsService = useSavedLocationsService();
  const { setLoading, overlay: loadingOverlay } = useLoadingOverlay();
  const { savedLocations } = useLocationsSaved();
  const createSavedLocation = useLocationsCreateSavedLocationFromShipment();

  // Returns if the broker has changed, hence we should reload
  async function show(): Promise<boolean> {
    let savedLocationId = findSavedLocationId(
      locationContext,
      shipment,
      savedLocations,
    );

    const originalLocation =
      locationContext === LocationContext.Pickup
        ? shipment.pickupLocation
        : shipment.deliveryLocation;

    const originalContact =
      locationContext === LocationContext.Pickup
        ? shipment.pickupContact
        : shipment.deliveryContact;

    const originalNotes =
      locationContext === LocationContext.Pickup
        ? shipment.pickupLocation.notes
        : shipment.deliveryLocation.notes;

    const originalReference =
      locationContext === LocationContext.Pickup
        ? shipment.pickupReferenceNumber
        : shipment.deliveryReferenceNumber;

    const originalBoothNumber =
      locationContext === LocationContext.Pickup
        ? shipment.pickupBoothNumber
        : shipment.deliveryBoothNumber;

    const originalAmazonReferenceId =
      locationContext === LocationContext.Delivery
        ? shipment.deliveryAmazonReferenceId
        : undefined;

    const originalAmazonShipmentId =
      locationContext === LocationContext.Delivery
        ? shipment.deliveryAmazonShipmentId
        : undefined;

    const updatedInfo = await referenceAndNotesDialog.show({
      shipmentId: shipment.shipmentId,
      referenceNumber: originalReference,
      amazonReferenceId: originalAmazonReferenceId,
      amazonShipmentId: originalAmazonShipmentId,
      boothNumber: originalBoothNumber,
      notes: originalNotes,
      distributionWarehouseBrand,
    });

    if (updatedInfo === undefined) {
      // Cancel pressed
      return false;
    }

    const isAmazonUpdate = updatedInfo.amazonReferenceId !== undefined;
    const referenceUnchanged = isAmazonUpdate
      ? originalAmazonReferenceId === updatedInfo.amazonReferenceId &&
        originalAmazonShipmentId === updatedInfo.amazonShipmentId
      : originalReference === updatedInfo.referenceNumber;

    if (originalNotes === updatedInfo.notes && referenceUnchanged) {
      // Nothing changed
      return false;
    }

    async function persistReferenceAndNotes(
      info: NonNullable<typeof updatedInfo>,
    ) {
      if (savedLocationId === undefined) {
        const locationWithUpdatedNotes = {
          ...originalLocation,
          notes: info.notes,
        };

        savedLocationId = await createSavedLocation(
          locationWithUpdatedNotes,
          originalContact,
        );
      } else {
        const savedLocation = savedLocations.find(
          (sl) => sl.savedLocationId === savedLocationId,
        );

        if (savedLocation === undefined) {
          throw new Error("Should not happen");
        }

        const locationToUpdate = savedLocation.location;
        locationToUpdate.notes = info.notes;

        // Make an API call to add the notes changed into the saved location
        await savedLocationsService.modifyLocation(
          savedLocationId,
          locationToUpdate,
        );
      }

      // Make an API call to change the reference/notes on the shipment
      await shipmentService.updateReferenceAndNotes(
        shipment.shipmentId,
        locationContext,
        info.referenceNumber,
        info.amazonReferenceId,
        info.amazonShipmentId,
        info.boothNumber,
        info.notes,
      );
    }

    setLoading(true);

    try {
      await persistReferenceAndNotes(updatedInfo);
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      setLoading(false);
    }

    // If the shipment is in transit, then it's too late to give the driver
    // a new BOL. But if it's still showing as pending pickup, it'd worth showing them this screen
    if (shipment.shipmentState !== ShipmentState.InTransit) {
      await updatedBOLDialog.show();
    }

    // Do we need to do something with the custom documents?
    // CS: Jan 2022: I'm currently taking the stance that we do nothing
    // - If they're not yet uploaded then nothing to do anyway
    // - If they have been uploaded, then they're still probably mostly valid, and hopefully
    //   the carrier can work with them
    // We should at some point give the customer the ability to upload replacement customs docs

    return true;
  }

  const dialogs = (
    <>
      {loadingOverlay}
      {referenceAndNotesDialog.dialogs}
      {updatedBOLDialog.dialogs}
    </>
  );

  return { show, dialogs };
}
